import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Bug, X, CheckCircle2, Loader2, AlertCircle, Send } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

// Small inline "report a bug" link. Sits underneath the user email pill
// in the navbar. The modal is portalled to <body> so it can't be clipped
// by ancestor `overflow:hidden` or stacking contexts (the original bug
// showed only the very top of the modal because it was nested inside
// the sticky navbar's stacking context).
export default function BugReportButton() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState("");
  const [expected, setExpected] = useState("");
  const [email, setEmail] = useState("");
  const [pageUrl, setPageUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [err, setErr] = useState("");
  const closeTimer = useRef(null);

  useEffect(() => {
    if (open) {
      try { setPageUrl(window.location.href); } catch { setPageUrl(""); }
      if (user?.email && !email) setEmail(user.email);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") doClose(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
  }, []);

  const doClose = () => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
    setOpen(false);
    setTimeout(() => {
      setDescription(""); setExpected(""); setErr(""); setSuccess(false); setSubmitting(false);
    }, 200);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (description.trim().length < 5) {
      setErr("Please describe what happened (at least 5 characters).");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/bug-reports", {
        page_url: pageUrl,
        description,
        expected: expected || null,
        email: email || null,
        user_agent: navigator.userAgent,
      });
      setSuccess(true);
      closeTimer.current = setTimeout(() => doClose(), 2000);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || e2.message || "Could not send the report.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Report a bug"
        aria-label="Report a bug"
        data-testid="report-bug-button"
        className="flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] font-mono text-[#00E5FF]/80 hover:text-[#00E5FF] transition-colors"
      >
        <Bug size={10} />
        report bug
      </button>

      {open && createPortal(
        <div
          className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-sm overflow-y-auto"
          onClick={doClose}
          data-testid="report-bug-modal-backdrop"
        >
          <div className="min-h-full flex items-center justify-center p-4">
            <div
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md bg-[#0A0A0A] text-zinc-100 border border-zinc-800 shadow-2xl"
              data-testid="report-bug-modal"
            >
              <div className="flex items-center justify-between px-5 py-3.5 border-b border-zinc-800">
                <div className="flex items-center gap-2">
                  <Bug size={15} className="text-[#00E5FF]" />
                  <h2 className="text-sm font-mono uppercase tracking-[0.2em] text-zinc-100">
                    Report a Bug
                  </h2>
                </div>
                <button
                  onClick={doClose}
                  className="text-zinc-500 hover:text-zinc-100"
                  aria-label="Close"
                  data-testid="report-bug-close"
                >
                  <X size={16} />
                </button>
              </div>

              {success ? (
                <div className="p-8 flex flex-col items-center text-center gap-3" data-testid="report-bug-success">
                  <CheckCircle2 size={36} className="text-[#22c55e]" />
                  <div className="font-display text-lg text-zinc-100">Thanks! We&apos;ll look into this.</div>
                  <div className="text-xs text-zinc-500 font-mono">closing…</div>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="p-5 space-y-3.5" data-testid="report-bug-form">
                  <Field label="What page were you on?">
                    <input
                      type="text"
                      value={pageUrl}
                      onChange={(e) => setPageUrl(e.target.value)}
                      className="w-full bg-black border border-zinc-800 focus:border-[#00E5FF] px-2.5 py-1.5 text-[11px] font-mono text-zinc-300 outline-none break-all"
                      data-testid="report-bug-url"
                      autoComplete="off"
                    />
                  </Field>
                  <Field label="What happened?" required>
                    <textarea
                      required
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={3}
                      placeholder="Tell us what went wrong…"
                      className="w-full bg-black border border-zinc-800 focus:border-[#00E5FF] px-2.5 py-2 text-sm text-zinc-100 outline-none resize-y"
                      data-testid="report-bug-description"
                    />
                  </Field>
                  <Field label="What did you expect to happen?">
                    <textarea
                      value={expected}
                      onChange={(e) => setExpected(e.target.value)}
                      rows={2}
                      placeholder="Optional"
                      className="w-full bg-black border border-zinc-800 focus:border-[#00E5FF] px-2.5 py-2 text-sm text-zinc-100 outline-none resize-y"
                      data-testid="report-bug-expected"
                    />
                  </Field>
                  <Field label="Your email">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="optional — so we can follow up"
                      className="w-full bg-black border border-zinc-800 focus:border-[#00E5FF] px-2.5 py-1.5 text-sm text-zinc-100 outline-none"
                      data-testid="report-bug-email"
                      autoComplete="email"
                    />
                  </Field>

                  {err && (
                    <div className="flex items-start gap-2 text-xs text-[#EF4444] bg-[#EF4444]/10 border border-[#EF4444]/30 p-2.5" data-testid="report-bug-error">
                      <AlertCircle size={14} className="shrink-0 mt-0.5" />
                      <span className="leading-relaxed">{err}</span>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-[#00E5FF] text-black font-semibold py-2.5 hover:bg-[#00B8CC] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    data-testid="report-bug-submit"
                  >
                    {submitting ? (
                      <><Loader2 size={14} className="animate-spin" /> sending…</>
                    ) : (
                      <>Send Report <Send size={13} /></>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

const Field = ({ label, required, children }) => (
  <label className="block space-y-1">
    <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">
      {label}{required ? " *" : ""}
    </span>
    {children}
  </label>
);
